import { formatPlaceTypePermission, usePlaceTypes } from '../../../../utils/helpers/placeTypes'

export const MAIN_COL_WIDTH = 12.5
export const ACTIONS_COL_WIDTH = 8
export const PADDING_COL_WIDTH = 1.5
export const COL_WIDTH = 5.5

const genCrudPermissions = name => [
  { key: 'list', name: `list${name}` },
  { key: 'create', name: `create${name}` },
  { key: 'edit', name: `edit${name}` },
  { key: 'delete', name: `delete${name}` }
]

const genImportExportPermissions = name => [
  { key: 'import', name: `import${name}` },
  { key: 'export', name: `export${name}` }
]

const genSetupPermissions = name => [{ key: 'setup', name: `setup${name}` }]

const genSimplePermission = name => ({ key: name, name })

const genPlacePermissions = name => [
  ...genCrudPermissions(name),
  ...genImportExportPermissions(name),
  ...genSetupPermissions(name),
  { key: 'createXsPlaceLevel', name: `create${name}sPlaceLevel` }
]

export const useModules = () => {
  const [placeTypes] = usePlaceTypes()

  return [
    {
      name: 'general',
      permissions: [
        genSimplePermission('seeRecordIds'), //
        genSimplePermission('useNumberedPagination'),
        genSimplePermission('setupScreen'),
        genSimplePermission('seeConfidentialFields'),
        genSimplePermission('showExpiredContractWarning'),
        genSimplePermission('showBillingWarning'),
        genSimplePermission('showQRCodeOnModalDetails'),
        genSimplePermission('blockPastDates'),
        genSimplePermission('useBtimeAI')
      ]
    },
    {
      name: 'dashboard',
      permissions: [
        genSimplePermission('seeDashboard'), //
        genSimplePermission('setupDashboard')
      ]
    },
    {
      name: 'users',
      permissions: [
        ...genCrudPermissions('User'), //
        ...genImportExportPermissions('User'),
        ...genSetupPermissions('User'),
        genSimplePermission('createUserGroup'),
        genSimplePermission('requireUserGroup'),
        genSimplePermission('changePassword')
      ]
    },
    {
      name: 'checklists',
      permissions: [
        ...genCrudPermissions('Checklist'),
        ...genImportExportPermissions('Checklist'),
        ...genSetupPermissions('Checklist'),
        genSimplePermission('createChecklistType'),
        genSimplePermission('requireChecklistType'),
        genSimplePermission('showUndoOption')
      ]
    },
    {
      name: 'serviceOrders',
      permissions: [
        ...genCrudPermissions('ServiceOrder'),
        ...genImportExportPermissions('ServiceOrder'),
        genSimplePermission('canEditServiceOrderImport'),
        ...genSetupPermissions('ServiceOrder'),
        genSimplePermission('setupServiceOrderTag'),
        genSimplePermission('editServiceOrdersName'),
        genSimplePermission('editServiceOrdersStatus'),
        genSimplePermission('canCreateSlaToServiceOrder'),
        genSimplePermission('seeServiceOrdersResume'),
        genSimplePermission('seeServiceOrdersPublicResumeLink'),
        genSimplePermission('useAutomaticDistribution'),
        genSimplePermission('useResumePdfHeaderImage'),
        genSimplePermission('executePublicServiceOrder'),
        genSimplePermission('seeOnlySelfServiceOrders'),
        genSimplePermission('createServiceOrderForOthers'),
        genSimplePermission('createServiceOrderWithoutUser'),
        genSimplePermission('useFastServiceOrder'),
        genSimplePermission('createLeanServiceOrder'),
        genSimplePermission('hideSaveOnLeanServiceOrder'),
        genSimplePermission('reviewServiceOrder'),
        genSimplePermission('validateDuplicateServiceOrders'),
        genSimplePermission('editFinishedServiceOrdersUser'),
        genSimplePermission('refuseServiceOrder'),
        genSimplePermission('hideServiceOrdersPlace'),
        genSimplePermission('requirePauseReason'),
        // TODO - remove permission after create config on kanbam
        genSimplePermission('canViewLabelTriggersCall'),
        genSimplePermission('notificationAlert'),
        genSimplePermission('createChild'),
        genSimplePermission('updateChecklistChildServiceOrder'),
        genSimplePermission('hideCheckinAndCheckout'),
        genSimplePermission('hideAttendanceTime'),
        genSimplePermission('canExecuteFromWeb'),
        genSimplePermission('bulkCreateServiceOrders'),
        genSimplePermission('canEditFinishedServiceOrders'),
        genSimplePermission('switchChecklistNameByYearlyId'),
        genSimplePermission('canUseDateKanbanSuggestion'),
        genSimplePermission('enablePlaceLock'),
        genSimplePermission('requireUnproductiveServiceOrderSignature'),
        genSimplePermission('validateDuplicateServiceOrdersOnApp'),
        genSimplePermission('canViewServiceOrderInternalId'),
        genSimplePermission('seeServiceOrdersTimeline'),
        genSimplePermission('seeServiceOrdersTriggerEvents'),
        genSimplePermission('seeServiceOrdersAuditPanel')
      ]
    },
    { name: 'forms', permissions: [...genCrudPermissions('Form'), { key: 'export', name: `exportFormReply` }] },

    {
      name: 'assets',
      permissions: [
        ...genCrudPermissions('Asset'), //
        ...genImportExportPermissions('Asset'),
        ...genSetupPermissions('Asset'),
        genSimplePermission('configureAssetQrCodeMenuRules')
      ]
    },
    {
      name: 'nonCompliances',
      permissions: [
        { key: 'list', name: 'listNonCompliance' },
        { key: 'completeNonCompliance', name: 'completeNonCompliance' },
        { key: 'reopenNonCompliance', name: 'reopenNonCompliance' }
      ]
    },
    {
      name: 'availability',
      permissions: [
        genSimplePermission('useAvailabilityPanel'), //
        genSimplePermission('editPlaceAvailabilityStatus')
      ]
    },
    { name: 'maps', permissions: [{ key: 'seeMap', name: 'seeMap' }] },
    { name: 'segments', permissions: [...genCrudPermissions('Segment')] },
    { name: 'triggers', permissions: [...genCrudPermissions('Trigger')] },
    { name: 'routines', permissions: [...genCrudPermissions('Routine')] },
    { name: 'publicToken', permissions: [...genCrudPermissions('PublicToken')] },
    {
      name: 'templates',
      permissions: [
        ...genCrudPermissions('Template'),
        {
          key: 'createTemplateRecord',
          name: 'createTemplateRecord'
        }
      ]
    },
    {
      name: 'integrations',
      permissions: [...genCrudPermissions(`Integration`), genSimplePermission('canUseTwilioIntegration')]
    },
    {
      name: 'masks',
      permissions: [...genCrudPermissions('Masks')]
    },
    {
      name: 'dynamicServiceOrders',
      permissions: [...genCrudPermissions('DynamicServiceOrder')]
    },
    ...placeTypes.map(type => ({
      name: type.namePlural,
      permissions: genPlacePermissions(formatPlaceTypePermission(type.key))
    })),
    {
      name: 'stocks',
      permissions: [...genCrudPermissions('Stocks'), genSimplePermission('removeGeneralStock')]
    }
  ]
}
